import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FormControlLabel, Grid, MenuItem, Switch, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { addBank, editBank, getBankById } from '../../../api/endpoints/system';
import type { Currency } from '../../../api/types/common';
import type { Bank } from '../../../api/types/system';
import { FormDialog } from '../../../components/common';
import { useFreshEditRow } from '../../../components/useFreshEditRow';

const CURRENCIES = ['TRY', 'USD', 'EUR', 'GBP', 'CHF', 'KZT'] as const;

const schema = z.object({
  bankName: z.string().min(1, 'Banka adı gerekli'),
  posCode: z.string().optional(),
  currency: z.string().optional(),
  isActive: z.boolean(),
  isDefault: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

const emptyValues: FormValues = { bankName: '', posCode: '', currency: 'TRY', isActive: true, isDefault: false };

function toFormValues(bank: Bank): FormValues {
  return {
    bankName: bank.bankName ?? '',
    posCode: bank.posCode ?? '',
    currency: bank.currency ?? 'TRY',
    isActive: !!bank.isActive,
    isDefault: !!bank.isDefault,
  };
}

/** Create/edit dialog for Banka Ayarları. Confirmed via local-backend swagger + live curl
 *  (see api/types/system.ts header). */
export function BankFormDialog({
  open,
  bank,
  onClose,
}: {
  open: boolean;
  /** null = create mode; otherwise edit mode for this row. */
  bank: Bank | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!bank;

  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: emptyValues });

  // Refetch the fresh row on edit-open; silently falls back to the list row on error
  // (Get*ById can 200 with a business-logic null-ref for some accounts' data).
  const seed = useFreshEditRow({
    open,
    editing: bank,
    queryKey: ['system', 'bank', bank?.id],
    fetch: async (row) => (await getBankById(row.id)).banks?.[0],
  });

  useEffect(() => {
    if (open) reset(seed ? toFormValues(seed) : emptyValues);
  }, [open, seed, reset]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) => {
      const typed = { ...values, currency: values.currency as Currency };
      return isEdit && bank ? editBank({ ...(seed ?? bank), ...typed }) : addBank(typed);
    },
    onSuccess: () => {
      enqueueSnackbar(isEdit ? 'Banka güncellendi.' : 'Banka oluşturuldu.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['system', 'banks'] });
      onClose();
    },
    onError: () => {
      /* surfaced via ApiErrorAlert below */
    },
  });

  const submit = handleSubmit((values) => mutation.mutate(values));

  return (
    <FormDialog
      open={open}
      title={isEdit ? 'Bankayı Güncelle' : 'Yeni Banka'}
      onClose={onClose}
      onSubmit={submit}
      submitting={mutation.isPending}
      error={mutation.error}
    >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Banka Adı"
                error={!!errors.bankName}
                helperText={errors.bankName?.message}
                {...register('bankName')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Pos Kodu" {...register('posCode')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                control={control}
                name="currency"
                render={({ field }) => (
                  <TextField {...field} select fullWidth size="small" label="Para Birimi">
                    {CURRENCIES.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Controller
                control={control}
                name="isActive"
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                    label="Aktif"
                  />
                )}
              />
              <Controller
                control={control}
                name="isDefault"
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                    label="Varsayılan"
                  />
                )}
              />
            </Grid>
          </Grid>
    </FormDialog>
  );
}
